import { StudentGQL } from "@schema/gql/student";
import { StudentSchemaType, StudentApiSchemaType } from "@schema/zod/student";
import { SchoolYearGQL } from "@schema/gql/schoolYear";
import { SchoolYearSchemaType } from "@schema/zod/schoolYear";
import { StudentSchoolYearGQL } from "@schema/gql/studentSchoolYear";
import { StudentSchoolYearSchemaType } from "@schema/zod/studentSchoolYear";
import { ScoreGQL, ScoreDetailGQL } from "@schema/gql/score";
import { Score, Subject, Competence } from "@schema/zod/score";
import { captureException } from "@/lib/error-tracker";

/**
 * Maps frontend blood type display values to backend enum values
 */
const mapBloodTypeToBackend = (displayValue: string): string => {
  const bloodTypeMap: { [key: string]: string } = {
    'A+': 'A_POSITIVE',
    'A-': 'A_NEGATIVE',
    'B+': 'B_POSITIVE', 
    'B-': 'B_NEGATIVE',
    'AB+': 'AB_POSITIVE',
    'AB-': 'AB_NEGATIVE',
    'O+': 'O_POSITIVE',
    'O-': 'O_NEGATIVE',
  };
  return bloodTypeMap[displayValue] || displayValue;
};

//Removes - from phone numbers
const normalizePhone = (value?: string): string | undefined => {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  return digits.length > 0 ? digits : undefined;
};

/**
 * Maps backend blood type enum values to frontend display values
 */
const mapBloodTypeToFrontend = (enumValue: string): string => {
  const bloodTypeMap: { [key: string]: string } = {
    'A_POSITIVE': 'A+',
    'A_NEGATIVE': 'A-',
    'B_POSITIVE': 'B+',
    'B_NEGATIVE': 'B-',
    'AB_POSITIVE': 'AB+',
    'AB_NEGATIVE': 'AB-',
    'O_POSITIVE': 'O+',
    'O_NEGATIVE': 'O-',
  };
  return bloodTypeMap[enumValue] || enumValue;
};

/**
 * Maps API student data (REST/GraphQL) to frontend schema
 */
export const mapToSchemaStudent = (apiStudent: StudentGQL): StudentSchemaType => {
  if (!apiStudent) {
    throw new Error("Cannot map undefined or null student");
  }


  try {
    // Debug logging for birthInfo and birthday mapping
    const mappedStudent = {
      registrationNumber: apiStudent.registryNumber ?? '',
      firstNames: apiStudent.name ?? '',
      fatherLastName: apiStudent.fatherLastname ?? '',
      motherLastName: apiStudent.motherLastname ?? '',
      
      course: apiStudent.gradeName ?? "",
      section: apiStudent.section ?? "",
      schoolYear: apiStudent.schoolYear ?? "",
      // paymentStatus: apiStudent.paymentStatus ?? undefined,
      // gradeStatus: apiStudent.gradeStatus ?? undefined,
      
      sector: typeof apiStudent.address === 'object' ? apiStudent.address?.sector ?? '' : '',
      address: typeof apiStudent.address === 'object' ? apiStudent.address?.detail ?? '' : '',
      
      phone: apiStudent.phone ?? '',
      mobile: apiStudent.mobile ?? '',
      email: apiStudent.inscriptorInfo?.email ?? '',
      
      idCard: apiStudent.ID ?? '',
      RNE: apiStudent.RNE ?? '',
      
      livesWith: (apiStudent.livesWith?.toLowerCase() as "father" | "mother" | "tutor" | "other" | undefined) ?? undefined,
      gender: (apiStudent.gender?.toLowerCase() as "m" | "f" | undefined) ?? undefined,
      
      birthDate: apiStudent.birthInfo?.birthday ??  undefined,
      birthProvince: apiStudent.birthInfo?.province ?? '',
      birthMunicipality: apiStudent.birthInfo?.municipality ?? '',
      
      nationality: apiStudent.nacionality ?? '',
      religion: apiStudent.religion ?? '',
      filiation: (apiStudent.inscriptorInfo?.filiation?.toLowerCase() as "father" | "mother" | "tutor" | "other" | "uncle/aunt" | "grandparent" | "sibling" | undefined) ?? undefined,
      
      previousSchool: apiStudent.previousSchool ?? '',
      enrollmentDataBook: apiStudent.enrollment?.book ?? '',
      enrollmentDataFolio: apiStudent.enrollment?.folio ?? '',
      
      pediatrician: apiStudent.medicalInfo?.pediatricianName ?? '',
      pediatricianPhone: apiStudent.medicalInfo?.pediatricianPhone ?? '',
      bloodType: apiStudent.medicalInfo?.bloodType ? mapBloodTypeToFrontend(apiStudent.medicalInfo.bloodType) : '',
      illnessOrAllergies: apiStudent.medicalInfo?.specialCondition ?? '',
      medication: Array.isArray(apiStudent.medicalInfo?.medications) 
        ? apiStudent.medicalInfo.medications.join(', ') 
        : apiStudent.medicalInfo?.medications ?? '',
      
      act: apiStudent.birthActInfo?.ID ?? '',
      actDate: apiStudent.birthActInfo?.date ? new Date(apiStudent.birthActInfo.date) : undefined,
      actProvince: apiStudent.birthActInfo?.province ?? '',
      actMunicipality: apiStudent.birthActInfo?.municipality ?? '',
      registryOffice: apiStudent.birthActInfo?.registryOffice ?? '',
      
      baptized: apiStudent.extraInfo?.baptized ?? false,
      confirmation: apiStudent.extraInfo?.confirmed ?? false,
      firstCommunion: apiStudent.extraInfo?.firstCommunion ?? false,
      medicalCertificate: apiStudent.extraInfo?.medicalCertificate ?? false,
      originalBirthCertificate: apiStudent.extraInfo?.birthCertificate ?? false,
      gradeRecord: apiStudent.extraInfo?.gradeRecord ?? false,
      gradeRecordError: apiStudent.extraInfo?.gradeRecordChecked ?? false,
      sixthGradeCertificate: apiStudent.extraInfo?.sixthGradeCertificate ?? false,
      clearanceLetter: apiStudent.extraInfo?.clearanceLetter ?? false,
      conductLetter: apiStudent.extraInfo?.conductLetter ?? false,
      resolution: apiStudent.extraInfo?.resolution ?? false,
      photo: apiStudent.extraInfo?.photo ?? false,
      
      observations: apiStudent.observations ?? '',
    };

    return mappedStudent;
  } catch (error) {
    captureException(error, {
      extra: { context: "Error in mapToSchemaStudent" }
    });
    throw error;
  }
};

/**
 * Maps frontend schema to API student data for creation (excludes school year fields)
 */
export const mapToApiStudentCreate = (schemaStudent: StudentApiSchemaType): Partial<StudentGQL> => {
  if (!schemaStudent) {
    throw new Error("Cannot map undefined or null student");
  }

  const student: Partial<StudentGQL> = {
    registryNumber: schemaStudent.registrationNumber,
    name: schemaStudent.firstNames,
    fatherLastname: schemaStudent.fatherLastName,
    ...(schemaStudent.motherLastName && schemaStudent.motherLastName.trim() !== '' && { motherLastname: schemaStudent.motherLastName }),
    // Note: grade, section, schoolYear are intentionally excluded for API creation
    
    ...(schemaStudent.address && {
      address: {
        sector: schemaStudent.sector,
        detail: schemaStudent.address
      }
    }),
    
    ...(normalizePhone(schemaStudent.phone) && { phone: normalizePhone(schemaStudent.phone) }),
    ...(normalizePhone(schemaStudent.mobile) && { mobile: normalizePhone(schemaStudent.mobile) }),
    
    ...(schemaStudent.idCard && { ID: schemaStudent.idCard }),
    ...(schemaStudent.RNE && { RNE: schemaStudent.RNE }),
    
    ...(schemaStudent.livesWith && { livesWith: schemaStudent.livesWith.toUpperCase() }),
    ...(schemaStudent.gender && { gender: schemaStudent.gender.toUpperCase() }),
    
    ...((schemaStudent.birthDate || schemaStudent.birthProvince || schemaStudent.birthMunicipality) && {
      birthInfo: {
        ...(schemaStudent.birthDate && { birthday: schemaStudent.birthDate }),
        ...(schemaStudent.birthProvince && { province: schemaStudent.birthProvince }),
        ...(schemaStudent.birthMunicipality && { municipality: schemaStudent.birthMunicipality })
      }
    }),
    
    ...(schemaStudent.nationality && { nacionality: schemaStudent.nationality }),
    ...(schemaStudent.religion && { religion: schemaStudent.religion }),
    
    ...((schemaStudent.filiation || schemaStudent.email) && {
      inscriptorInfo: {
        ...(schemaStudent.filiation && { filiation: schemaStudent.filiation.toUpperCase() }),
        ...(schemaStudent.email && { email: schemaStudent.email })
      }
    }),
    
    // Add all other optional fields...
    ...(schemaStudent.previousSchool && { previousSchool: schemaStudent.previousSchool }),
    // TODO:remove from student page? paymentStatus and gradeStatus are not enabled because we dont know if they make sense or not
    // ...(schemaStudent.paymentStatus && { paymentStatus: schemaStudent.paymentStatus }),
    // ...(schemaStudent.gradeStatus && { gradeStatus: schemaStudent.gradeStatus }),

    ...(schemaStudent.enrollmentDataBook && {
      enrollment: {
        book: schemaStudent.enrollmentDataBook,
        ...(schemaStudent.enrollmentDataFolio && { folio: schemaStudent.enrollmentDataFolio })
      }
    }),

    ...((schemaStudent.pediatrician || schemaStudent.pediatricianPhone || schemaStudent.bloodType || schemaStudent.illnessOrAllergies || schemaStudent.medication) && {
      medicalInfo: {
        ...(schemaStudent.pediatrician && { pediatricianName: schemaStudent.pediatrician }),
        ...(schemaStudent.pediatricianPhone && { pediatricianPhone: schemaStudent.pediatricianPhone }),
        ...(schemaStudent.bloodType && { bloodType: mapBloodTypeToBackend(schemaStudent.bloodType) }),
        ...(schemaStudent.illnessOrAllergies && { specialCondition: schemaStudent.illnessOrAllergies }),
        ...(schemaStudent.medication && { medications: [schemaStudent.medication] })
      }
    }),

    ...((schemaStudent.act || schemaStudent.actDate || schemaStudent.actProvince || schemaStudent.actMunicipality || schemaStudent.registryOffice) && {
      birthActInfo: {
        ...(schemaStudent.act && { ID: schemaStudent.act }),
        ...(schemaStudent.actDate && { date: schemaStudent.actDate }),
        ...(schemaStudent.actProvince && { province: schemaStudent.actProvince }),
        ...(schemaStudent.actMunicipality && { municipality: schemaStudent.actMunicipality }),
        ...(schemaStudent.registryOffice && { registryOffice: schemaStudent.registryOffice })
      }
    }),

    ...((schemaStudent.baptized || schemaStudent.confirmation || schemaStudent.firstCommunion || schemaStudent.medicalCertificate ||
        schemaStudent.originalBirthCertificate || schemaStudent.gradeRecord || schemaStudent.gradeRecordError ||
        schemaStudent.sixthGradeCertificate || schemaStudent.clearanceLetter ||
        schemaStudent.conductLetter || schemaStudent.resolution || schemaStudent.photo) && {
      extraInfo: {
        ...(schemaStudent.baptized !== undefined && { baptized: schemaStudent.baptized }),
        ...(schemaStudent.confirmation !== undefined && { confirmed: schemaStudent.confirmation }),
        ...(schemaStudent.firstCommunion !== undefined && { firstCommunion: schemaStudent.firstCommunion }),
        ...(schemaStudent.medicalCertificate !== undefined && { medicalCertificate: schemaStudent.medicalCertificate }),
        ...(schemaStudent.originalBirthCertificate !== undefined && { birthCertificate: schemaStudent.originalBirthCertificate }),
        ...(schemaStudent.gradeRecord !== undefined && { gradeRecord: schemaStudent.gradeRecord }),
        ...(schemaStudent.gradeRecordError !== undefined && { gradeRecordChecked: schemaStudent.gradeRecordError }),
        ...(schemaStudent.sixthGradeCertificate !== undefined && { sixthGradeCertificate: schemaStudent.sixthGradeCertificate }),
        ...(schemaStudent.clearanceLetter !== undefined && { clearanceLetter: schemaStudent.clearanceLetter }),
        ...(schemaStudent.conductLetter !== undefined && { conductLetter: schemaStudent.conductLetter }),
        ...(schemaStudent.resolution !== undefined && { resolution: schemaStudent.resolution }),
        ...(schemaStudent.photo !== undefined && { photo: schemaStudent.photo })
      }
    }),

    ...(schemaStudent.observations && { observations: schemaStudent.observations }),
  };

  return student;
};

export const mapLocalPropertyToApiPath = (property: string) => {
  const mapper = {
    registrationNumber: 'registryNumber',
    firstNames: 'name',
    fatherLastName: 'fatherLastname',
    motherLastName: 'motherLastname',
    course: 'grade',
    section: 'section',
    schoolYear: 'schoolYear',
    address: 'address.detail',
    sector: 'address.sector',
    phone: 'phone',
    mobile: 'mobile',
    idCard: 'idCard',
    RNE: 'RNE',
    livesWith: 'livesWith',
    gender: 'gender',
    birthDate: 'birthInfo.birthday',
    birthProvince: 'birthInfo.province',
    birthMunicipality: 'birthInfo.municipality',
    nationality: 'nationality',
    religion: 'religion',
    filiation: 'inscriptorInfo.filiation',
    email: 'inscriptorInfo.email',
    previousSchool: 'previousSchool',
    enrollmentDataBook: 'enrollment.book',
    enrollmentDataFolio: 'enrollment.detail',
    pediatrician: 'medicalInfo.pediatricianName',
    pediatricianPhone: 'medicalInfo.pediatricianPhone',
    bloodType: 'medicalInfo.bloodType',
    illnessOrAllergies: 'medicalInfo.specialCondition',
    medication: 'medicalInfo.medications',
    act: 'birthActInfo.ID',
    actDate: 'birthActInfo.date',
    actProvince: 'birthActInfo.province',
    actMunicipality: 'birthActInfo.municipality',
    registryOffice: 'birthActInfo.registryOffice',
    baptized: 'extraInfo.baptized',
    confirmation: 'extraInfo.confirmed',
    firstCommunion: 'extraInfo.firstCommunion',
    medicalCertificate: 'extraInfo.medicalCertificate',
    originalBirthCertificate: 'extraInfo.birthCertificate',
    gradeRecord: 'extraInfo.gradeRecord',
    gradeRecordError: 'extraInfo.gradeRecordChecked',
    sixthGradeCertificate: 'extraInfo.sixthGradeCertificate',
    clearanceLetter: 'extraInfo.clearanceLetter',
    conductLetter: 'extraInfo.conductLetter',
    resolution: 'extraInfo.resolution',
    photo: 'extraInfo.photo',
  };

  return mapper[property as keyof typeof mapper] || null;
};

/**
 * Maps frontend schema to API student data (REST/GraphQL) - includes school year fields for backwards compatibility
 */
export const mapToApiStudent = (schemaStudent: StudentSchemaType): Partial<StudentGQL> => {
  if (!schemaStudent) {
    throw new Error("Cannot map undefined or null student");
  }

  const student: Partial<StudentGQL> = {
    registryNumber: schemaStudent.registrationNumber,
    name: schemaStudent.firstNames,
    fatherLastname: schemaStudent.fatherLastName,
    ...(schemaStudent.motherLastName && schemaStudent.motherLastName.trim() !== '' && { motherLastname: schemaStudent.motherLastName }),
    grade:schemaStudent.course,
    section:schemaStudent.section,
    schoolYear:schemaStudent.schoolYear,
    
    ...(schemaStudent.address && {
      address: {
        sector: schemaStudent.sector,
        detail: schemaStudent.address
      }
    }),
    
    ...(normalizePhone(schemaStudent.phone) && { phone: normalizePhone(schemaStudent.phone) }),
    ...(normalizePhone(schemaStudent.mobile) && { mobile: normalizePhone(schemaStudent.mobile) }),
    
    ...(schemaStudent.idCard && { ID: schemaStudent.idCard }),
    ...(schemaStudent.RNE && { RNE: schemaStudent.RNE }),
    
    ...(schemaStudent.livesWith && { livesWith: schemaStudent.livesWith.toUpperCase() }),
    ...(schemaStudent.gender && { gender: schemaStudent.gender.toUpperCase() }),
    
    ...((schemaStudent.birthDate || schemaStudent.birthProvince || schemaStudent.birthMunicipality) && {
      birthInfo: {
        ...(schemaStudent.birthDate && { birthday: schemaStudent.birthDate }),
        ...(schemaStudent.birthProvince && { province: schemaStudent.birthProvince }),
        ...(schemaStudent.birthMunicipality && { municipality: schemaStudent.birthMunicipality })
      }
    }),
    
    ...(schemaStudent.nationality && { nacionality: schemaStudent.nationality }),
    ...(schemaStudent.religion && { religion: schemaStudent.religion }),
    
    ...((schemaStudent.filiation || schemaStudent.email) && {
      inscriptorInfo: {
        ...(schemaStudent.filiation && { filiation: schemaStudent.filiation.toUpperCase() }),
        ...(schemaStudent.email && { email: schemaStudent.email })
      }
    }),
    
    ...(schemaStudent.previousSchool && { previousSchool: schemaStudent.previousSchool }),
    
    ...(schemaStudent.enrollmentDataBook && {
      enrollment: {
        book: schemaStudent.enrollmentDataBook,
        ...(schemaStudent.enrollmentDataFolio && { detail: schemaStudent.enrollmentDataFolio })
      }
    }),
    
    ...((schemaStudent.pediatrician || schemaStudent.pediatricianPhone || schemaStudent.bloodType || schemaStudent.illnessOrAllergies || schemaStudent.medication) && {
      medicalInfo: {
        ...(schemaStudent.pediatrician && { pediatricianName: schemaStudent.pediatrician }),
        ...(schemaStudent.pediatricianPhone && { pediatricianPhone: schemaStudent.pediatricianPhone }),
        ...(schemaStudent.bloodType && { bloodType: mapBloodTypeToBackend(schemaStudent.bloodType) }),
        ...(schemaStudent.illnessOrAllergies && { specialCondition: schemaStudent.illnessOrAllergies }),
        ...(schemaStudent.medication && { medications: [schemaStudent.medication] })
      }
    }),
    
    ...((schemaStudent.act || schemaStudent.actDate || schemaStudent.actProvince || schemaStudent.actMunicipality || schemaStudent.registryOffice) && {
      birthActInfo: {
        ...(schemaStudent.act && { ID: schemaStudent.act }),
        ...(schemaStudent.actDate && { date: schemaStudent.actDate }),
        ...(schemaStudent.actProvince && { province: schemaStudent.actProvince }),
        ...(schemaStudent.actMunicipality && { municipality: schemaStudent.actMunicipality }),
        ...(schemaStudent.registryOffice && { registryOffice: schemaStudent.registryOffice })
      }
    }),
    
    ...((schemaStudent.baptized || schemaStudent.confirmation || schemaStudent.firstCommunion || schemaStudent.medicalCertificate || 
        schemaStudent.originalBirthCertificate || schemaStudent.gradeRecord || schemaStudent.gradeRecordError || 
        schemaStudent.sixthGradeCertificate || schemaStudent.clearanceLetter || 
        schemaStudent.conductLetter || schemaStudent.resolution || schemaStudent.photo) && {
      extraInfo: {
        ...(schemaStudent.baptized !== undefined && { baptized: schemaStudent.baptized }),
        ...(schemaStudent.confirmation !== undefined && { confirmed: schemaStudent.confirmation }),
        ...(schemaStudent.firstCommunion !== undefined && { firstCommunion: schemaStudent.firstCommunion }),
        ...(schemaStudent.medicalCertificate !== undefined && { medicalCertificate: schemaStudent.medicalCertificate }),
        ...(schemaStudent.originalBirthCertificate !== undefined && { birthCertificate: schemaStudent.originalBirthCertificate }),
        ...(schemaStudent.gradeRecord !== undefined && { gradeRecord: schemaStudent.gradeRecord }),
        ...(schemaStudent.gradeRecordError !== undefined && { gradeRecordChecked: schemaStudent.gradeRecordError }),
        ...(schemaStudent.sixthGradeCertificate !== undefined && { sixthGradeCertificate: schemaStudent.sixthGradeCertificate }),
        ...(schemaStudent.clearanceLetter !== undefined && { clearanceLetter: schemaStudent.clearanceLetter }),
        ...(schemaStudent.conductLetter !== undefined && { conductLetter: schemaStudent.conductLetter }),
        ...(schemaStudent.resolution !== undefined && { resolution: schemaStudent.resolution }),
        ...(schemaStudent.photo !== undefined && { photo: schemaStudent.photo })
      }
    }),
    
    ...(schemaStudent.observations && { observations: schemaStudent.observations })
  };

  return student;
};

/**
 * Maps API school year data (REST/GraphQL) to frontend schema
 */
export const mapToSchemaSchoolYear = (apiSchoolYear: SchoolYearGQL): SchoolYearSchemaType => {
  if (!apiSchoolYear) {
    throw new Error("Cannot map undefined or null school year");
  }

  try {
    const mappedSchoolYear = {
      code: apiSchoolYear.code ?? '',
      start: apiSchoolYear.start ? new Date(apiSchoolYear.start) : new Date(),
      end: apiSchoolYear.end ? new Date(apiSchoolYear.end) : new Date(),
      date: apiSchoolYear.date ? new Date(apiSchoolYear.date) : new Date()
    };

    return mappedSchoolYear;
  } catch (error) {
    captureException(error, {
      extra: { context: "Error in mapToSchemaSchoolYear" }
    });
    throw error;
  }
};

/**
 * Maps API score data (GraphQL) to frontend schema
 * Handles the transformation from string identifiers to numeric IDs
 */
export const mapToSchemaScore = (
  apiScore: ScoreGQL, 
  subjects: Subject[], 
  competences: Competence[]
): Score => {
  if (!apiScore) {
    throw new Error("Cannot map undefined or null score");
  }

  try {
    // Find the corresponding subject and competence by their detail/name
    const subject = subjects.find((s) => s.detail === apiScore.subject);
    const competence = competences.find((c) => c.detail === apiScore.competence);

    const mappedScore: Score = {
      code: apiScore.code,
      score: apiScore.score,
      period: apiScore.period,
      competenceID: competence?.ID || apiScore.competence || 0,
      subjectID: subject?.ID || 0
    };

    return mappedScore;
  } catch (error) {
    captureException(error, {
      extra: { context: "Error in mapToSchemaScore" }
    });
    throw error;
  }
};

/**
 * Maps API score detail data (GraphQL) to frontend schema with extended information
 */
export const mapToSchemaScoreDetail = (
  apiScore: ScoreDetailGQL,
  subjects: Subject[],
  competences: Competence[]
): Score & { section?: string; gradeName?: string; teacher?: string; date?: string } => {
  if (!apiScore) {
    throw new Error("Cannot map undefined or null score detail");
  }

  try {
    // Use the base score mapping first
    const baseScore = mapToSchemaScore(apiScore, subjects, competences);

    // Add the extended fields
    return {
      ...baseScore,
      section: apiScore.section,
      gradeName: apiScore.gradeName,
      teacher: apiScore.teacher,
      date: apiScore.date
    };
  } catch (error) {
    captureException(error, {
      extra: { context: "Error in mapToSchemaScoreDetail" }
    });
    throw error;
  }
};

/**
 * Maps multiple API scores to frontend schema
 */
export const mapToSchemaScores = (
  apiScores: ScoreGQL[],
  subjects: Subject[],
  competences: Competence[]
): Score[] => {
  if (!Array.isArray(apiScores)) {
    return [];
  }

  return apiScores.map((score) => mapToSchemaScore(score, subjects, competences));
};